/**
 * SLAP BATTLES - Casino Audio Manager
 * Uses Howler.js for professional, crisp casino-style sound effects
 */

import { Howl, Howler } from 'howler';

// Enable spatial audio for 3D positioning
Howler.spatial = false;

// Sound effect definitions with Web Audio synthesis fallback
const SOUND_DEFINITIONS = {
  // === GAMEPLAY SOUNDS ===
  tapSuccess: {
    // Coins clinking sound - satisfying casino chip hit
    synth: { type: 'tap', freq: 2400, duration: 0.08 },
    volume: 0.6
  },
  tapGood: {
    // Higher pitched coin for good targets
    synth: { type: 'coin', freq: 3200, duration: 0.12 },
    volume: 0.7
  },
  bombHit: {
    // Glass breaking + bass thud
    synth: { type: 'explosion', freq: 80, duration: 0.4 },
    volume: 0.8
  },
  jackpot: {
    // Slot machine win fanfare
    synth: { type: 'jackpot', freq: 440, duration: 0.8 },
    volume: 0.9
  },
  freeze: {
    // Ice/freeze effect
    synth: { type: 'freeze', freq: 150, duration: 0.5 },
    volume: 0.7
  },
  
  // === BUFF SOUNDS ===
  shieldActivate: {
    synth: { type: 'shield', freq: 800, duration: 0.3 },
    volume: 0.6
  },
  shieldPop: {
    synth: { type: 'pop', freq: 400, duration: 0.2 },
    volume: 0.7
  },
  slowMotion: {
    synth: { type: 'slowmo', freq: 200, duration: 0.6 },
    volume: 0.5
  },
  multiplierHit: {
    synth: { type: 'sparkle', freq: 1800, duration: 0.15 },
    volume: 0.5
  },
  
  // === UI SOUNDS ===
  countdown: {
    synth: { type: 'beep', freq: 600, duration: 0.15 },
    volume: 0.5
  },
  countdownGo: {
    synth: { type: 'fanfare', freq: 880, duration: 0.3 },
    volume: 0.7
  },
  gameOver: {
    synth: { type: 'gameover', freq: 300, duration: 0.6 },
    volume: 0.6
  },
  victory: {
    synth: { type: 'victory', freq: 523, duration: 1.2 },
    volume: 0.8
  },
  chestOpen: {
    synth: { type: 'chest', freq: 400, duration: 0.4 },
    volume: 0.7
  },
  coinRain: {
    synth: { type: 'coinrain', freq: 2000, duration: 0.6 },
    volume: 0.6
  }
};

class AudioManager {
  constructor() {
    this.enabled = true;
    this.masterVolume = 1.0;
    this.audioContext = null;
    this.sounds = {};
    this.initialized = false;
  }

  // Initialize audio context on first user interaction
  init() {
    if (this.initialized) return;
    
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.initialized = true;
      console.log('[AudioManager] Initialized with Web Audio API');
    } catch (e) {
      console.warn('[AudioManager] Web Audio not available:', e);
    }
  }

  // Set master volume (0-1)
  setVolume(vol) {
    this.masterVolume = Math.max(0, Math.min(1, vol));
    Howler.volume(this.masterVolume);
  }

  // Toggle sound on/off
  toggle(enabled) {
    this.enabled = enabled;
    if (!enabled) {
      Howler.mute(true);
    } else {
      Howler.mute(false);
    }
  }

  // === SYNTHESIZED SOUND GENERATION ===
  // Creates crisp, casino-quality sounds without external files
  
  synthesize(soundDef) {
    if (!this.enabled || !this.audioContext) return;
    
    const ctx = this.audioContext;
    const { type, freq, duration } = soundDef.synth;
    const volume = (soundDef.volume || 0.5) * this.masterVolume;
    
    const masterGain = ctx.createGain();
    masterGain.connect(ctx.destination);
    masterGain.gain.value = volume;
    
    const now = ctx.currentTime;
    
    switch(type) {
      case 'tap':
        // Sharp, satisfying tap - like a casino chip hitting felt
        this._createTap(ctx, masterGain, now, freq, duration);
        break;
        
      case 'coin':
        // Coin clink with metallic resonance
        this._createCoin(ctx, masterGain, now, freq, duration);
        break;
        
      case 'explosion':
        // Glass shatter + bass thud
        this._createExplosion(ctx, masterGain, now, freq, duration);
        break;
        
      case 'jackpot':
        // Slot machine win - ascending tones with sparkle
        this._createJackpot(ctx, masterGain, now, freq, duration);
        break;
        
      case 'freeze':
        // Ice crystallization sound
        this._createFreeze(ctx, masterGain, now, freq, duration);
        break;
        
      case 'shield':
        // Energy shield activation
        this._createShield(ctx, masterGain, now, freq, duration);
        break;
        
      case 'pop':
        // Bubble pop for shield break
        this._createPop(ctx, masterGain, now, freq, duration);
        break;
        
      case 'slowmo':
        // Time warp effect
        this._createSlowMo(ctx, masterGain, now, freq, duration);
        break;
        
      case 'sparkle':
        // Multiplier sparkle
        this._createSparkle(ctx, masterGain, now, freq, duration);
        break;
        
      case 'beep':
        // Clean countdown beep
        this._createBeep(ctx, masterGain, now, freq, duration);
        break;
        
      case 'fanfare':
        // GO! fanfare
        this._createFanfare(ctx, masterGain, now, freq, duration);
        break;
        
      case 'gameover':
        // Game over descending tone
        this._createGameOver(ctx, masterGain, now, freq, duration);
        break;
        
      case 'victory':
        // Victory fanfare
        this._createVictory(ctx, masterGain, now, freq, duration);
        break;
        
      case 'chest':
        // Treasure chest opening
        this._createChest(ctx, masterGain, now, freq, duration);
        break;
        
      case 'coinrain':
        // Multiple coins falling
        this._createCoinRain(ctx, masterGain, now, freq, duration);
        break;
        
      default:
        this._createBeep(ctx, masterGain, now, freq, duration);
    }
  }

  // === SOUND GENERATORS ===
  
  _createTap(ctx, dest, now, freq, dur) {
    // Sharp attack, quick decay - like hitting a chip
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    filter.type = 'highpass';
    filter.frequency.value = 1500;
    
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(dest);
    
    osc.frequency.setValueAtTime(freq, now);
    osc.frequency.exponentialRampToValueAtTime(freq * 2, now + dur * 0.2);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.5, now + dur);
    
    gain.gain.setValueAtTime(0.8, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
    
    osc.start(now);
    osc.stop(now + dur);
  }

  _createCoin(ctx, dest, now, freq, dur) {
    // Metallic coin sound with harmonics
    [1, 2.5, 4].forEach((harmonic, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(dest);
      
      osc.frequency.value = freq * harmonic;
      osc.type = 'triangle';
      
      const level = 0.4 / (i + 1);
      gain.gain.setValueAtTime(level, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + dur * (1 - i * 0.2));
      
      osc.start(now);
      osc.stop(now + dur);
    });
  }

  _createExplosion(ctx, dest, now, freq, dur) {
    // Noise burst + bass thump
    const bufferSize = ctx.sampleRate * dur;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    
    // White noise with decay
    for (let i = 0; i < bufferSize; i++) {
      const decay = 1 - (i / bufferSize);
      data[i] = (Math.random() * 2 - 1) * decay * decay;
    }
    
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(4000, now);
    filter.frequency.exponentialRampToValueAtTime(100, now + dur);
    
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(1, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(dest);
    
    // Add bass thud
    const bass = ctx.createOscillator();
    const bassGain = ctx.createGain();
    bass.connect(bassGain);
    bassGain.connect(dest);
    
    bass.frequency.setValueAtTime(freq, now);
    bass.frequency.exponentialRampToValueAtTime(20, now + dur);
    bass.type = 'sine';
    
    bassGain.gain.setValueAtTime(0.8, now);
    bassGain.gain.exponentialRampToValueAtTime(0.01, now + dur);
    
    noise.start(now);
    bass.start(now);
    bass.stop(now + dur);
  }

  _createJackpot(ctx, dest, now, freq, dur) {
    // Ascending arpeggio with sparkle
    const notes = [1, 1.25, 1.5, 2]; // Major chord + octave
    notes.forEach((ratio, i) => {
      const delay = i * 0.1;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(dest);
      
      osc.frequency.value = freq * ratio;
      osc.type = 'triangle';
      
      gain.gain.setValueAtTime(0, now + delay);
      gain.gain.linearRampToValueAtTime(0.5, now + delay + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
      
      osc.start(now + delay);
      osc.stop(now + dur + 0.1);
    });
    
    // Add sparkle (high frequency noise bursts)
    for (let i = 0; i < 5; i++) {
      const sparkle = ctx.createOscillator();
      const sGain = ctx.createGain();
      sparkle.connect(sGain);
      sGain.connect(dest);
      
      sparkle.frequency.value = 3000 + Math.random() * 2000;
      sparkle.type = 'sine';
      
      const sTime = now + i * 0.15;
      sGain.gain.setValueAtTime(0.15, sTime);
      sGain.gain.exponentialRampToValueAtTime(0.001, sTime + 0.08);
      
      sparkle.start(sTime);
      sparkle.stop(sTime + 0.1);
    }
  }

  _createFreeze(ctx, dest, now, freq, dur) {
    // Crystalline ice sound
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(2000, now);
    filter.Q.value = 5;
    
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(dest);
    
    osc.frequency.setValueAtTime(freq * 8, now);
    osc.frequency.exponentialRampToValueAtTime(freq, now + dur);
    osc.type = 'sawtooth';
    
    gain.gain.setValueAtTime(0.4, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
    
    osc.start(now);
    osc.stop(now + dur);
  }

  _createShield(ctx, dest, now, freq, dur) {
    // Energy shield whoosh
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(dest);
    
    osc.frequency.setValueAtTime(freq * 0.5, now);
    osc.frequency.exponentialRampToValueAtTime(freq * 2, now + dur * 0.5);
    osc.frequency.exponentialRampToValueAtTime(freq, now + dur);
    osc.type = 'sine';
    
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.linearRampToValueAtTime(0.5, now + dur * 0.3);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
    
    osc.start(now);
    osc.stop(now + dur);
  }

  _createPop(ctx, dest, now, freq, dur) {
    // Bubble pop
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(dest);
    
    osc.frequency.setValueAtTime(freq * 3, now);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.3, now + dur);
    osc.type = 'sine';
    
    gain.gain.setValueAtTime(0.6, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
    
    osc.start(now);
    osc.stop(now + dur);
  }

  _createSlowMo(ctx, dest, now, freq, dur) {
    // Time warp - descending whoosh
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, now);
    filter.frequency.exponentialRampToValueAtTime(100, now + dur);
    
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(dest);
    
    osc.frequency.setValueAtTime(freq * 4, now);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.25, now + dur);
    osc.type = 'sawtooth';
    
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
    
    osc.start(now);
    osc.stop(now + dur);
  }

  _createSparkle(ctx, dest, now, freq, dur) {
    // High sparkle for multiplier
    [1, 1.5, 2].forEach((ratio, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(dest);
      
      osc.frequency.value = freq * ratio;
      osc.type = 'sine';
      
      gain.gain.setValueAtTime(0.2 / (i + 1), now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
      
      osc.start(now);
      osc.stop(now + dur);
    });
  }

  _createBeep(ctx, dest, now, freq, dur) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(dest);
    
    osc.frequency.value = freq;
    osc.type = 'sine';
    
    gain.gain.setValueAtTime(0.4, now);
    gain.gain.setValueAtTime(0.4, now + dur * 0.8);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
    
    osc.start(now);
    osc.stop(now + dur);
  }

  _createFanfare(ctx, dest, now, freq, dur) {
    // GO! sound - major chord burst
    [1, 1.25, 1.5].forEach((ratio) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(dest);
      
      osc.frequency.value = freq * ratio;
      osc.type = 'triangle';
      
      gain.gain.setValueAtTime(0.5, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
      
      osc.start(now);
      osc.stop(now + dur);
    });
  }

  _createGameOver(ctx, dest, now, freq, dur) {
    // Descending sad tones
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(dest);
    
    osc.frequency.setValueAtTime(freq, now);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.5, now + dur * 0.5);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.25, now + dur);
    osc.type = 'triangle';
    
    gain.gain.setValueAtTime(0.5, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
    
    osc.start(now);
    osc.stop(now + dur);
  }

  _createVictory(ctx, dest, now, freq, dur) {
    // Triumphant ascending arpeggio
    const notes = [1, 1.25, 1.5, 2, 2.5, 3]; // Extended major
    notes.forEach((ratio, i) => {
      const delay = i * 0.12;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(dest);
      
      osc.frequency.value = freq * ratio;
      osc.type = i % 2 === 0 ? 'triangle' : 'sine';
      
      gain.gain.setValueAtTime(0, now + delay);
      gain.gain.linearRampToValueAtTime(0.4, now + delay + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
      
      osc.start(now + delay);
      osc.stop(now + dur + 0.2);
    });
  }

  _createChest(ctx, dest, now, freq, dur) {
    // Chest creak + reveal sparkle
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(dest);
    
    osc.frequency.setValueAtTime(freq * 0.5, now);
    osc.frequency.exponentialRampToValueAtTime(freq * 2, now + dur * 0.3);
    osc.frequency.setValueAtTime(freq * 3, now + dur * 0.5);
    osc.type = 'sawtooth';
    
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.5, now + dur * 0.3);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
    
    osc.start(now);
    osc.stop(now + dur);
  }

  _createCoinRain(ctx, dest, now, freq, dur) {
    // Multiple coin sounds in sequence
    for (let i = 0; i < 8; i++) {
      const delay = i * 0.07 + Math.random() * 0.03;
      const coinFreq = freq + Math.random() * 1000;
      
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(dest);
      
      osc.frequency.value = coinFreq;
      osc.type = 'triangle';
      
      const coinGain = 0.2 + Math.random() * 0.1;
      gain.gain.setValueAtTime(coinGain, now + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.1);
      
      osc.start(now + delay);
      osc.stop(now + delay + 0.12);
    }
  }

  // === PUBLIC API ===
  
  play(soundName) {
    if (!this.enabled) return;
    
    this.init(); // Ensure initialized on first play
    
    const def = SOUND_DEFINITIONS[soundName];
    if (def) {
      this.synthesize(def);
    } else {
      console.warn(`[AudioManager] Unknown sound: ${soundName}`);
    }
  }
}

// Singleton instance
const audioManager = new AudioManager();

export default audioManager;
export { audioManager };
